import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { 
  Users, Search, Eye, RefreshCw, 
  User, MapPin, Briefcase, GraduationCap, CreditCard, UserCheck,
  CheckCircle, Clock, AlertCircle, Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDateDDMMYYYY } from '@/utils/textFormatting';
import axios from 'axios';
import CandidateDraftViewerReadOnly from './CandidateDraftViewerReadOnly';

export default function CandidateDraftsTabHR() {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDraft, setSelectedDraft] = useState(null);
  const [viewerOpen, setViewerOpen] = useState(false);

  useEffect(() => {
    fetchDrafts();
  }, []);

  const fetchDrafts = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/candidate-drafts');
      setDrafts(response.data);
    } catch (error) {
      toast.error('Failed to load candidate drafts');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDraft = (draft) => {
    setSelectedDraft(draft);
    setViewerOpen(true);
  };

  const handleCloseViewer = () => {
    setViewerOpen(false);
    setSelectedDraft(null);
  };

  const filteredDrafts = drafts.filter(draft => {
    const search = searchTerm.toLowerCase();
    return (
      draft.name?.toLowerCase().includes(search) ||
      draft.email?.toLowerCase().includes(search) ||
      draft.case_id?.toLowerCase().includes(search)
    );
  });

  const getProgressColor = (percentage) => {
    if (percentage >= 80) return 'text-emerald-600';
    if (percentage >= 50) return 'text-amber-600';
    return 'text-rose-600';
  };

  const getProgressBadge = (percentage) => {
    if (percentage >= 80) return { className: 'bg-emerald-500 text-white', label: 'Almost Complete' };
    if (percentage >= 50) return { className: 'bg-amber-500 text-white', label: 'In Progress' };
    if (percentage > 0) return { className: 'bg-orange-500 text-white', label: 'Started' };
    return { className: 'bg-slate-200 text-slate-600', label: 'Not Started' };
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Employee Data Entry Status</h2>
          <p className="text-slate-600">Track employees who are filling their verification details</p>
        </div>
        <Button onClick={fetchDrafts} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-slate-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total In Progress</p>
                <p className="text-2xl font-bold text-slate-900">{drafts.length}</p>
              </div>
              <Users className="w-8 h-8 text-slate-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-emerald-600">Almost Done (80%+)</p>
                <p className="text-2xl font-bold text-emerald-700">
                  {drafts.filter(d => d.progress?.percentage >= 80).length}
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-emerald-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-amber-600">In Progress</p>
                <p className="text-2xl font-bold text-amber-700">
                  {drafts.filter(d => d.progress?.percentage >= 20 && d.progress?.percentage < 80).length}
                </p>
              </div>
              <Clock className="w-8 h-8 text-amber-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-rose-200 bg-rose-50">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-rose-600">Just Started</p>
                <p className="text-2xl font-bold text-rose-700">
                  {drafts.filter(d => d.progress?.percentage < 20).length}
                </p>
              </div>
              <AlertCircle className="w-8 h-8 text-rose-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Search by name, email, or case ID..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Drafts List */}
      {loading ? (
        <div className="text-center py-8 text-slate-500">Loading...</div>
      ) : filteredDrafts.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center">
            <CheckCircle className="w-12 h-12 text-emerald-300 mx-auto mb-4" />
            <p className="text-slate-600 font-medium">All employees have completed their data entry!</p>
            <p className="text-slate-500 text-sm mt-1">
              {searchTerm ? 'No employees match your search' : 'No pending data entries at this time'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredDrafts.map((draft) => {
            const progressBadge = getProgressBadge(draft.progress?.percentage || 0);
            return (
              <Card key={draft.id} className="border-slate-200 hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    {/* Left: Employee Info */}
                    <div className="flex items-center gap-4 flex-1">
                      <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center">
                        <User className="w-6 h-6 text-slate-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-slate-900 truncate">{draft.name}</h3>
                          <Badge className={progressBadge.className}>{progressBadge.label}</Badge>
                        </div>
                        <p className="text-sm text-slate-500 truncate">{draft.email}</p>
                        <div className="flex items-center gap-4 mt-1 text-xs text-slate-400">
                          {draft.case_id && (
                            <span>Case: {draft.case_id}</span>
                          )}
                          {draft.invitation_sent_at && (
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              Started: {formatDateDDMMYYYY(draft.invitation_sent_at)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Middle: Progress */}
                    <div className="w-48 px-4">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-slate-500">Progress</span>
                        <span className={`text-sm font-semibold ${getProgressColor(draft.progress?.percentage || 0)}`}>
                          {draft.progress?.percentage || 0}%
                        </span>
                      </div>
                      <Progress value={draft.progress?.percentage || 0} className="h-2" />
                    </div>

                    {/* Right: Actions */}
                    <div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewDraft(draft)}
                        data-testid={`view-draft-${draft.id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View Details
                      </Button>
                    </div>
                  </div>

                  {/* Section Progress Icons */}
                  <div className="flex items-center gap-4 mt-3 pt-3 border-t border-slate-100">
                    <div className={`flex items-center gap-1 text-xs ${draft.progress?.sections?.basic ? 'text-emerald-600' : 'text-slate-400'}`}>
                      <User className="w-3 h-3" />
                      Basic
                    </div>
                    <div className={`flex items-center gap-1 text-xs ${draft.progress?.sections?.address ? 'text-emerald-600' : 'text-slate-400'}`}>
                      <MapPin className="w-3 h-3" />
                      Address
                    </div>
                    <div className={`flex items-center gap-1 text-xs ${draft.progress?.sections?.employment ? 'text-emerald-600' : 'text-slate-400'}`}>
                      <Briefcase className="w-3 h-3" />
                      Employment
                    </div>
                    <div className={`flex items-center gap-1 text-xs ${draft.progress?.sections?.education ? 'text-emerald-600' : 'text-slate-400'}`}>
                      <GraduationCap className="w-3 h-3" />
                      Education
                    </div>
                    <div className={`flex items-center gap-1 text-xs ${draft.progress?.sections?.id_details ? 'text-emerald-600' : 'text-slate-400'}`}>
                      <CreditCard className="w-3 h-3" />
                      ID Details
                    </div>
                    <div className={`flex items-center gap-1 text-xs ${draft.progress?.sections?.references ? 'text-emerald-600' : 'text-slate-400'}`}>
                      <UserCheck className="w-3 h-3" />
                      References
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Viewer Dialog */}
      {selectedDraft && (
        <CandidateDraftViewerReadOnly
          open={viewerOpen}
          onClose={handleCloseViewer}
          employeeId={selectedDraft.id}
        />
      )}
    </div>
  );
}
